import React, { memo, useState } from 'react';
import { CTA } from '../../interfaces/CTA.interface';
import { Blok } from '../../interfaces/Blok.interface';
import { storyblokEditable } from 'gatsby-source-storyblok';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import StoryblokLinkProvider from '../Utils/StoryblokLinkProvider/StoryblokLinkProvider';
import CustomLink from '../CustomLink/CustomLink';
import { StoryblokLink } from '../../interfaces/StoryblokLink.interface';
import classNames from 'classnames';
import {
  DataLayerData,
  pushToDataLayer,
} from '../../utils/pushToDataLayer/pushToDataLayer.util';

interface GetQuoteDropdownButtonBlokProps extends Blok {
  label: string;
  link: StoryblokLink;
  dropdown_options: string[];
  size: ButtonSize;
}

interface GetQuoteDropdownButtonProps {
  blok: GetQuoteDropdownButtonBlokProps;
}

const getFormatedDropdownLabel = (unformattedDropdownLabel: string): string => {
  const capitalizedUnformattedDropdownLabel: string =
    unformattedDropdownLabel[0].toUpperCase() +
    unformattedDropdownLabel.slice(1);
  return capitalizedUnformattedDropdownLabel
    .replaceAll('-', ' ')
    .replaceAll(' and ', ' & ');
};

const GetQuoteDropdownButton: React.FC<GetQuoteDropdownButtonProps> = ({
  blok,
}) => {
  const { label, link, dropdown_options, size } = blok;
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  const handleDataLayerClickEvent = (label: string): void => {
    pushToDataLayer({
      event: 'quote_dropdown',
      data: {
        quote_funnel_journey: label,
        quote_cta_url: window.location.href,
      },
    });
  };

  return (
    <div {...storyblokEditable(blok)} className="relative w-full sm:w-auto">
      <ButtonLink
        url={''}
        label={label}
        type={ButtonType.PRIMARY}
        size={size || ButtonSize.LG}
        className="flex items-center justify-center gap-x-2 w-full"
        onClick={() => setIsDropdownVisible(!isDropdownVisible)}
      >
        <span>{label}</span>
        <span
          className={classNames('h-6 w-6 bg-no-repeat bg-text/body', {
            'rotate-180 transition-transform duration-200 ease-in-out':
              isDropdownVisible,
            'rotate-0 transition-transform duration-200 ease-in-out':
              !isDropdownVisible,
          })}
          style={{
            WebkitMaskImage: "url('/svgs/chevron.svg')",
            maskImage: "url('/svgs/chevron.svg')",
          }}
        />
      </ButtonLink>
      {isDropdownVisible && link && dropdown_options && (
        <div className="absolute left-0 right-0 z-10 shadow-lg">
          <div className="w-full flex items-center justify-center">
            <div className="w-12 flex items-center justify-center overflow-hidden -translate-x-2">
              <div className="h-6 w-6 bg-system/white rotate-45 transform origin-bottom-left" />
            </div>
          </div>
          <ul className="w-full bg-system/white py-4">
            {dropdown_options.map((dropdown_option: string) => (
              <li key={dropdown_option}>
                <StoryblokLinkProvider
                  link={link}
                  render={(link: string) => (
                    <CustomLink
                      url={`${link}?type=${dropdown_option}`}
                      label={getFormatedDropdownLabel(dropdown_option)}
                      className="block px-10 py-3 font-bold text-left text-sm hover:text-text/body-accent"
                      onClick={() => {
                        handleDataLayerClickEvent(
                          getFormatedDropdownLabel(dropdown_option)
                        );
                        setIsDropdownVisible(false);
                      }}
                    >
                      {getFormatedDropdownLabel(dropdown_option)}
                    </CustomLink>
                  )}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default memo(GetQuoteDropdownButton);
